import { QueryStatus, useQuery } from "@tanstack/react-query";

import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { LockedCard } from "@/components/layout/locked-content";

import { message } from "@/lib/utils";
import { ConsumerResearchApiFactory } from "@/apis/brand-os/consumer-research";
import { ConsumerResearchCollectionPerformanceItem, ConsumerResearchSentimentBreakdown } from "@/schema/brand-os/ai-consumer-research";

const compactFormatter = new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 });
const percentFormatter = new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 });

export function SubBrandMetrics() {
  const { data, status } = useQuery(ConsumerResearchApiFactory.Queries.CollectionPerformance());
  return <SubBrandMetricsComponent status={status} collections={data?.collections ?? []} />;
}

function SubBrandMetricsComponent({ status, collections }: { status: QueryStatus; collections: ConsumerResearchCollectionPerformanceItem[] }) {
  switch (status) {
    case "pending":
      return <SubBrandMetricsSkeleton />;
    case "error":
      return <LockedCard title="Sub-Brand Performance" message={message} className="rounded-2xl" />;
    case "success":
      return collections.length ? <SubBrandMetricsContent collections={collections} /> : <LockedCard title="Sub-Brand Performance" message={message} className="rounded-2xl" />;
  }
}

function SubBrandMetricsContent({ collections }: { collections: ConsumerResearchCollectionPerformanceItem[] }) {
  const getSentimentPercentages = (breakdown: ConsumerResearchSentimentBreakdown) => {
    const total = breakdown.positive + breakdown.neutral + breakdown.negative;
    if (total <= 0) return { positive: 0, neutral: 0, negative: 0 };
    return { positive: breakdown.positive / total, neutral: breakdown.neutral / total, negative: breakdown.negative / total };
  };

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
      {collections.map((item) => {
        const sentiment = getSentimentPercentages(item.sentiment_breakdown);
        return (
          <Card key={item.collection_name} className="rounded-3xl border-border/60 shadow-none">
            <CardContent className="space-y-5 p-5">
              <div className="flex items-start justify-between gap-4">
                <p className="text-base font-semibold text-foreground">{item.collection_name}</p>
                <div className="text-right">
                  <p className="text-2xl font-bold text-foreground">{item.nps}</p>
                  <p className="text-xs text-muted-foreground">NPS Score</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-2xl bg-muted/35 p-4">
                  <p className="text-xs text-muted-foreground">Mentions</p>
                  <p className="mt-2 text-sm font-bold text-foreground">{compactFormatter.format(item.total_mentions)}</p>
                </div>
                <div className="rounded-2xl bg-muted/35 p-4">
                  <p className="text-xs text-muted-foreground">Share of Mentions</p>
                  <p className="mt-2 text-sm font-bold text-foreground">{percentFormatter.format(item.share_of_mentions)}%</p>
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">Sentiment Split</p>
                <div className="flex h-2 overflow-hidden rounded-full bg-muted/60">
                  <div className="bg-success" style={{ width: `${sentiment.positive * 100}%` }} />
                  <div className="bg-warning" style={{ width: `${sentiment.neutral * 100}%` }} />
                  <div className="bg-destructive" style={{ width: `${sentiment.negative * 100}%` }} />
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs font-medium">
                  <p className="text-success">{percentFormatter.format(sentiment.positive * 100)}% Positive</p>
                  <p className="text-center text-warning">{percentFormatter.format(sentiment.neutral * 100)}% Neutral</p>
                  <p className="text-right text-destructive">{percentFormatter.format(sentiment.negative * 100)}% Negative</p>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function SubBrandMetricsSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
      {Array.from({ length: 6 }, (_, i) => (
        <Card key={i} className="rounded-3xl border-border/60 shadow-none">
          <CardContent className="space-y-5 p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <Skeleton className="h-5 w-28 rounded-md" />
                <Skeleton className="h-3 w-16 rounded-md" />
              </div>
              <div className="text-right">
                <Skeleton className="h-8 w-12 rounded-md" />
                <Skeleton className="mt-1 h-3 w-16 rounded-md" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-2xl bg-muted/35 p-4">
                <Skeleton className="h-3 w-14 rounded-md" />
                <Skeleton className="mt-2 h-4 w-20 rounded-md" />
              </div>
              <div className="rounded-2xl bg-muted/35 p-4">
                <Skeleton className="h-3 w-24 rounded-md" />
                <Skeleton className="mt-2 h-4 w-16 rounded-md" />
              </div>
            </div>

            <div className="space-y-2">
              <Skeleton className="h-3 w-20 rounded-md" />
              <Skeleton className="h-2 w-full rounded-full" />
              <div className="grid grid-cols-3 gap-2">
                <Skeleton className="h-3 w-20 rounded-md" />
                <Skeleton className="h-3 w-20 rounded-md justify-self-center" />
                <Skeleton className="h-3 w-20 rounded-md justify-self-end" />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
